import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, RouterModule } from '@angular/router';
import { StudentJobStudentApplication } from '@edtech-ui/core/models/student-job-management/get-all-my-applications';
import { StudentJobService } from '@edtech-ui/core/services/student-job-management/student-job.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-jobs-my-applications',
	standalone: true,
	imports: [RouterModule, CommonModule, MatProgressSpinnerModule],
	templateUrl: './jobs-my-applications.component.html',
	styleUrl: './jobs-my-applications.component.scss'
})
export class JobsMyApplicationsComponent {

	jobData: StudentJobStudentApplication[] = [];
	subscription = new Subscription;
	total: number = 0;
	totalPages: number = 0;
	page: number = 1;
	limit: number = 10;
	isLoading: boolean = false;


	constructor(
		private router: Router,
		private studentJobService: StudentJobService,
		private toastr: ToastrService
	) { }


	ngOnInit(): void {
		this.getAllStudentApplications();
	}

	@HostListener('document:visibilitychange', ['$event'])
	onVisibilityChange(): void {
		if (document.visibilityState === 'visible') {
			if (!this.isLoading) {
				this.page = 1;
				this.getAllStudentApplications();
			}
		}
	}

	getAllStudentApplications() {
		this.isLoading = true;
		this.subscription.add(
			this.studentJobService.getAllStudentApplications(
				this.page,
				this.limit
			).pipe(
				tap(response => {
					if (response.Success) {

						if (this.page === 1) {
							this.jobData = response.Data?.applications || [];
						} else {
							this.jobData = [...this.jobData, ...(response.Data?.applications || [])];
						}
						this.jobData.sort((a, b) => {
							const dateA = a.applied_at ? new Date(a.applied_at).getTime() : 0;
							const dateB = b.applied_at ? new Date(b.applied_at).getTime() : 0;
							return dateB - dateA;
						});
						this.total = response.Data?.total || 0;
						this.page = response.Data?.page || 1;
						this.limit = response.Data?.limit || 10;
						this.totalPages = response.Data?.total_pages || 0;
					} else {
						this.toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this.toastr.error('Something went wrong.Please try again');
					return of(null);
				})
			).subscribe()
		)
	}


	goToAssesmentRules() {
		this.router.navigate(['/student-dashboard/jobs/assessment-rules']);
	}


	goToInterviewRules() {
		this.router.navigate(['/student-dashboard/jobs/ai-interview-rules']);
	}


	loadMore() {
		if (this.page < this.totalPages && !this.isLoading) {
			this.page++;
			this.getAllStudentApplications();
		}
	}


	goToJobDashboard() {
		this.router.navigate(['/student-dashboard/jobs']);
	}


	openAssessment(application: StudentJobStudentApplication) {
		const assessment = application.assessment;
		if (!assessment?.quiz_link_url) {
			this.toastr.error('Invalid assessment URL');
			return;
		}

		if (assessment.end_date) {
			const endDate = new Date(assessment.end_date);
			const now = new Date();
			if (now >= endDate) {
				this.toastr.error('This assessment link is no longer active', 'Assessment link expired');
				return;
			}
		}
		try {
			const assessmentUrl = new URL(assessment.quiz_link_url);
			assessmentUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(assessmentUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid assessment URL:', assessment.quiz_link_url);
			this.toastr.error('Invalid assessment URL');
		}
	}


	openInterview(application: StudentJobStudentApplication) {
		const interview = application.interview;

		if (!interview?.interview_url) {
			this.toastr.error('Invalid interview URL');
			return;
		}

		if (interview.end_date) {
			const endDate = new Date(interview.end_date);
			const now = new Date();
			if (now >= endDate) {
				this.toastr.error('This interview link is no longer active','Interview link expired');
				return;
			}
		}
		try {
			const interviewUrl = new URL(interview.interview_url);
			interviewUrl.searchParams.set('isStudentDashboard', 'true');
			window.open(interviewUrl.toString(), '_blank');
		} catch (error) {
			console.error('Invalid interview URL:', interview.interview_url);
			this.toastr.error('Invalid interview URL');
		}
	}

	showAssessment(application: StudentJobStudentApplication): boolean {
		return application.assessment?.status !== 'not_assigned';
	}


	showInterview(application: StudentJobStudentApplication): boolean {
		return application.interview?.status !== 'not_assigned';
	}


	showAssessmentButton(application: StudentJobStudentApplication): boolean {
		return application.assessment?.status === 'pending';
	}


	showInterviewButton(application: StudentJobStudentApplication): boolean {
		if (application.interview?.status !== 'pending') {
			return false;
		}
		if (!application.assessment || application.assessment.status === 'not_assigned') {
			return true;
		}
		return !!application.assessment.completed_at;
	}

	isAllStepsCompleted(application: StudentJobStudentApplication | null | undefined): boolean {
		if (!application) return false;

		const assessmentAssigned = !!application.assessment && application.assessment.status !== 'not_assigned';
		const interviewAssigned = !!application.interview && application.interview.status !== 'not_assigned';
		if (!assessmentAssigned && !interviewAssigned) {
			return false;
		}
		const assessmentCompleted = !assessmentAssigned || !!application.assessment?.completed_at;
		const interviewCompleted = !interviewAssigned || !!application.interview?.completed_at;
		return assessmentCompleted && interviewCompleted;
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}


}